import { supabase } from './supabase.js';
import type { AdherenceEvent } from '@nostos/shared';

export interface ExercisePick {
  id: string;
  name: string;
  bodyRegion: string | null;
  category: string | null;
}

export interface AssignmentItemInput {
  exerciseId: string;
  sets?: number;
  reps?: number;
  holdSeconds?: number;
}

/** Exercises the clinician can see (seed + own) via RLS. */
export async function listExercises(): Promise<ExercisePick[]> {
  const { data, error } = await supabase
    .from('exercises')
    .select('id, name, body_region, category')
    .eq('is_active', true)
    .order('name');
  if (error) throw new Error(error.message);
  return (data ?? []).map((e) => ({
    id: e.id,
    name: e.name,
    bodyRegion: e.body_region,
    category: e.category,
  }));
}

/** Calls the create-assignment edge function (server generates + hashes the token). */
export async function createAssignment(
  title: string,
  items: AssignmentItemInput[],
): Promise<{ assignmentId: string; token: string }> {
  const { data, error } = await supabase.functions.invoke('create-assignment', {
    body: { title, items },
  });
  if (error) throw new Error(error.message);
  return data as { assignmentId: string; token: string };
}

/** Reads adherence events for one assignment (RLS: only the owning clinician). */
export async function getAdherence(assignmentId: string): Promise<AdherenceEvent[]> {
  // Alias snake_case columns to camelCase so the result matches AdherenceEvent
  // (PostgREST returns snake_case and supabase-js does not auto-camelize).
  const { data, error } = await supabase
    .from('adherence_events')
    .select(
      'id, assignmentId:assignment_id, assignmentItemId:assignment_item_id, eventType:event_type, detail, occurredAt:occurred_at, receivedAt:received_at',
    )
    .eq('assignment_id', assignmentId)
    .order('received_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as AdherenceEvent[];
}
